import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { getEmployees } from '../services/profileService';

const STORAGE_KEY = 'selected_employee';
const SelectedEmployeeContext = createContext(null);

function loadPersistedSelection() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

export function SelectedEmployeeProvider({ children }) {
  const { isAdmin } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployeeState] = useState(() => isAdmin ? loadPersistedSelection() : null);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  const setSelectedEmployee = useCallback((emp) => {
    setSelectedEmployeeState(emp);
    if (emp) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(emp));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    if (!isAdmin) return;
    setLoadingEmployees(true);
    try {
      const data = await getEmployees();
      setEmployees(data || []);
    } catch {
      setEmployees([]);
    } finally {
      setLoadingEmployees(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) {
      fetchEmployees();
    } else {
      // Non-admin: clear any stale selection
      setSelectedEmployee(null);
    }
  }, [isAdmin, fetchEmployees, setSelectedEmployee]);

  const clearSelection = useCallback(() => {
    setSelectedEmployee(null);
  }, [setSelectedEmployee]);

  // The username to use for data-fetching: selected employee's username or null (self)
  const targetUsername = selectedEmployee?.username || null;

  const value = useMemo(
    () => ({
      employees,
      selectedEmployee,
      setSelectedEmployee,
      clearSelection,
      loadingEmployees,
      fetchEmployees,
      targetUsername,
    }),
    [employees, selectedEmployee, setSelectedEmployee, clearSelection, loadingEmployees, fetchEmployees, targetUsername]
  );

  return (
    <SelectedEmployeeContext.Provider value={value}>
      {children}
    </SelectedEmployeeContext.Provider>
  );
}

export function useSelectedEmployee() {
  const context = useContext(SelectedEmployeeContext);
  if (!context) {
    throw new Error('useSelectedEmployee must be used within a SelectedEmployeeProvider');
  }
  return context;
}
